import { FC, useEffect, useMemo, useState } from 'react'

import {
  DevChart,
  DevPanel,
  DevPanelMessage,
  DevPanelToggle
} from '../DevPanel'
import styles from './MemoryPanel.module.scss'
import { MemoryPanelProps } from './MemoryPanel.types'

const MAX_LIFETIME_POINTS = 3600
const MB = 1024 * 1024
const COLOR = 'rgb(99,102,241)'

const VIEW_OPTIONS = [
  { value: 'last1min', label: '1 min' },
  { value: 'lifetime', label: 'Lifetime' }
]

type MemoryPoint = { used: number; total: number; limit: number }
type View = 'last1min' | 'lifetime'

function readMemory(): MemoryPoint | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mem = (performance as any).memory
  if (!mem) return null
  return {
    used: mem.usedJSHeapSize / MB,
    total: mem.totalJSHeapSize / MB,
    limit: mem.jsHeapSizeLimit / MB
  }
}

function fmt(n: number): string {
  return n.toFixed(1)
}

export const MemoryPanel: FC<MemoryPanelProps> = ({ onClose }) => {
  const [history, setHistory] = useState<MemoryPoint[]>(() => {
    const p = readMemory()
    return p ? [p] : []
  })
  const [view, setView] = useState<View>('last1min')

  useEffect(() => {
    const id = setInterval(() => {
      const point = readMemory()
      if (!point) return
      setHistory(prev => {
        const next = [...prev, point]
        return next.length > MAX_LIFETIME_POINTS
          ? next.slice(-MAX_LIFETIME_POINTS)
          : next
      })
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const displayedHistory = useMemo(
    () => (view === 'last1min' ? history.slice(-60) : history),
    [history, view]
  )

  const chartData = useMemo(
    () => displayedHistory.map(d => d.used),
    [displayedHistory]
  )

  const latest = history[history.length - 1]
  const supported = !!latest

  return (
    <DevPanel
      title='Memory'
      defaultX={window.innerWidth - 400}
      defaultY={80}
      currentValue={
        supported ? (
          <span className={styles.currentValue}>
            {fmt(latest.used)}
            <span className={styles.currentUnit}> MB</span>
          </span>
        ) : undefined
      }
      actions={
        <DevPanelToggle
          options={VIEW_OPTIONS}
          value={view}
          onChange={v => setView(v as View)}
        />
      }
      onClose={onClose}
      footer={
        supported ? (
          <>
            <span>Used: {fmt(latest.used)} MB</span>
            <span>Heap: {fmt(latest.total)} MB</span>
            <span>Limit: {fmt(latest.limit)} MB</span>
          </>
        ) : undefined
      }
    >
      {!supported ? (
        <DevPanelMessage>
          Memory API unavailable (Chrome / Edge only)
        </DevPanelMessage>
      ) : displayedHistory.length < 2 ? (
        <DevPanelMessage>Collecting data…</DevPanelMessage>
      ) : (
        <DevChart
          data={chartData}
          totalPoints={history.length}
          color={COLOR}
          unit='MB'
        />
      )}
    </DevPanel>
  )
}
